<template>
  <div class="checkbox-filters">
    <filter-card-wrapper :title="title" :expanded="expanded">
      <div
        v-if="options.length > 5 || query.length"
        slot="header"
        class="checkbox-filters__header">
        <text-input
          placeholder="Type your search term..."
          :value="query"
          :show-close="true"
          @onChange="onSearch"
          @onClear="onClear" />
      </div>
      <div
        v-if="loading"
        slot="body"
        class="checkbox-filters__body checkbox-loader">
        <div class="vld-background" style="backdrop-filter: blur(2px)"></div>
        <div class="vld-icon">
          <svg
            viewBox="0 0 38 38"
            xmlns="http://www.w3.org/2000/svg"
            width="64"
            height="64"
            stroke="#0573AB">
            <g fill="none" fill-rule="evenodd">
              <g transform="translate(1 1)" stroke-width="2">
                <circle stroke-opacity=".25" cx="18" cy="18" r="18" />
                <path d="M36 18c0-9.94-8.06-18-18-18">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 18 18"
                    to="360 18 18"
                    dur="0.8s"
                    repeatCount="indefinite" />
                </path>
              </g>
            </g>
          </svg>
        </div>
        <h5>Fetching options...</h5>
      </div>
      <div
        v-else-if="currentOptions.length"
        slot="body"
        class="checkbox-filters__body"
        :class="{ scroll: currentOptions.length > 5 }">
        <checkbox
          v-for="(option, key) in currentOptions"
          :id="key"
          :key="key"
          v-tooltip="
            fieldData.fieldName === 'chargeCode'
              ? getChargeCodeValue(option.value)
              : option.value
          "
          :label="
            fieldData.fieldName === 'chargeCode'
              ? getChargeCodeValue(option.value)
              : option.value
          "
          :value="
            fieldData.fieldName === 'chargeCode'
              ? getChargeCodeValue(option.value)
              : option.value
          "
          :count="option.count"
          :checked="values.indexOf(option.value) > -1"
          :title="title"
          @onchange="onCheckboxChange($event, option.value, title)" />
      </div>
      <div
        v-else
        slot="body"
        class="checkbox-filters__body"
        :class="{ scroll: currentOptions.length > 5 }">
        <div class="rich-text">
          <p>No result found</p>
        </div>
      </div>
      <div
        v-if="!loading && computedOptions.length > 0"
        slot="footer"
        class="checkbox-filters__footer">
        <div class="checkbox-filters__footer__count-selection-area">
          <div
            v-if="options.length"
            class="checkbox-filters__footer__count-selection-area--count-section">
            {{
              selectedVisibleValues.length == options.length
                ? `Including all ${selectedVisibleValues.length}`
                : `Including ${selectedVisibleValues.length} of ${options.length}`
            }}
          </div>
          <div
            v-if="selectedVisibleValues.length === options.length"
            class="checkbox-filters__footer__count-selection-area--select-all"
            @click="onCheckboxMultiSelect('selectNone', title)">
            <span> Select none </span>
          </div>
          <div
            v-else
            class="checkbox-filters__footer__count-selection-area--select-all"
            @click="onCheckboxMultiSelect('selectAll', title, options)">
            <span> Select all </span>
          </div>
        </div>
        <div class="checkbox-filters__footer__show-area">
          <span
            v-if="showMoreContentVisible"
            @click="loadPaginatedData('SHOW_MORE')">
            Show more
          </span>
          <span
            v-if="showLessContentVisible"
            @click="loadPaginatedData('SHOW_LESS')">
            Show less
          </span>
        </div>
      </div>
    </filter-card-wrapper>
  </div>
</template>

<script>
// Maersk Icons
import FilterCardWrapper from "./FilterCardWrapper";

export default {
  name: "CheckboxFilter",
  components: {
    FilterCardWrapper,
  },
  props: {
    title: { type: String, required: true },
    expanded: { type: Boolean, default: false },
    options: { type: Array, required: true },
    values: { type: Array, required: false, default: () => {} },
    fieldData: { type: Object, required: true },
    loading: { type: Boolean, required: true },
  },
  data() {
    return {
      query: "",
      showMoreContentVisible: false,
      showLessContentVisible: false,
      currentOptions: [],
      selectedVisibleValues: [],
    };
  },
  computed: {
    computedOptions() {
      return this.query
        ? this.options.filter((option) => {
            return option.value
              ?.toString()
              .toLowerCase()
              .match(this.query.toLowerCase());
          })
        : this.options;
    },
  },
  watch: {
    options() {
      this.constructOptionsVisiblity();
    },
    values() {
      this.getActiveVisibleOptions();
    },
    computedOptions() {
      if (!this.computedOptions.length && this.query.length) {
        this.$emit("onSearchUpdateFacetIfNotFound", {
          query: this.query,
          facet: this.fieldData.fieldName,
        });
      }
    },
    expanded() {
      if (!this.expanded) {
        this.query = "";
        this.$emit("onSearchUpdateFacetIfNotFound", {
          query: this.query,
          facet: this.fieldData.fieldName,
        });
      }
    },
  },
  mounted() {
    this.constructOptionsVisiblity();
  },
  methods: {
    onCheckboxChange(isChecked, selectedValue, selectedKey) {
      this.$parent.$listeners.onCheckboxSelect(
        isChecked,
        selectedValue,
        selectedKey,
        this.fieldData
      );
    },
    onCheckboxMultiSelect(selectionType, selectedKey, currentOptions) {
      const selectedOptions = currentOptions
        ? currentOptions.map((option) => option.value)
        : [];

      this.$parent.$listeners.onCheckboxMultiSelect(
        selectionType,
        selectedKey,
        this.fieldData,
        selectedOptions
      );
    },
    onSearch(e) {
      this.query = e.target.value;
      this.$emit("onSearchUpdateFacetIfNotFound", {
        query: this.query,
        facet: this.fieldData.fieldName,
      });
      this.constructOptionsVisiblity();
    },
    onClear() {
      this.query = "";
      this.$emit("onClearFacet", {
        query: this.query,
        facet: this.fieldData.fieldName,
      });
      this.constructOptionsVisiblity();
    },
    loadPaginatedData(type) {
      if (type === "SHOW_MORE") {
        let count = this.currentOptions.length + 5;
        this.currentOptions = this.computedOptions.slice(0, count);
        this.showMoreContentVisible =
          this.computedOptions.length > this.currentOptions.length;
        this.showLessContentVisible = this.currentOptions.length > 5;
      } else {
        let count = this.currentOptions.length - 5;
        this.currentOptions = this.computedOptions.slice(0, count);
        this.showMoreContentVisible =
          this.computedOptions.length > this.currentOptions.length;
        this.showLessContentVisible = this.currentOptions.length > 5;
      }
    },
    constructOptionsVisiblity() {
      this.currentOptions = this.computedOptions?.slice(0, 5);
      this.filterCount = this.currentOptions.length;
      this.showMoreContentVisible =
        this.computedOptions.length > this.currentOptions.length;
      this.showLessContentVisible = this.currentOptions.length > 5;
      this.getActiveVisibleOptions();
    },
    getActiveVisibleOptions() {
      this.selectedVisibleValues = this.computedOptions.filter((option) => {
        return this.values.indexOf(option.value) > -1;
      });
    },
    getChargeCodeValue(value) {
      let chargeCodeValue;
      value.split("+").forEach((value) => {
        chargeCodeValue = chargeCodeValue
          ? chargeCodeValue + "+" + value?.split("-")?.[0]
          : value?.split("-")?.[0];
      });
      return chargeCodeValue;
    },
  },
};
</script>

<style lang="scss" scoped>
.checkbox-filters {
  display: flex;
  & .checkbox-filters__header {
    margin-top: 16px;
  }
  & .checkbox-filters__body {
    margin-top: 16px;
    &.checkbox-loader {
      text-align: center;
    }
    &.scroll {
      max-height: 200px;
      overflow-y: auto;
    }
  }
  & .checkbox-filters__footer {
    display: flex;
    flex-direction: column;
    & .checkbox-filters__footer__count-selection-area {
      justify-content: space-between;
      display: flex;
      margin-top: 12px;
      flex: 1;
      & .checkbox-filters__footer__count-selection-area--count-section {
        font-family: "Maersk Text";
        font-size: 14px;
        line-height: 16px;
        color: var(--Gray-600);
      }
      & .checkbox-filters__footer__count-selection-area--select-all {
        font-family: "Maersk Text";
        font-size: 14px;
        line-height: 16px;
        color: var(--Blue-600-Dark);
        cursor: pointer;
        text-decoration: underline;
        & span {
          cursor: pointer;
        }
      }
    }
    & .checkbox-filters__footer__show-area {
      display: flex;
      justify-content: center;
      & span {
        margin-top: 12px;
        font-family: "Maersk Text";
        font-size: 14px;
        line-height: 16px;
        color: var(--Blue-600-Dark);
        cursor: pointer;
        text-decoration: underline;
        &:nth-child(2) {
          margin-left: 24px;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.form-checkbox__label {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 280px;
}
</style>
